import { Link } from "@inertiajs/react"
import { ChevronLeft, Eye } from "lucide-react"
import { useTranslation } from "react-i18next"

/**
 * OKS-400 — Read-only profile-preview banner.
 *
 * Adapts the `acting-as-banner` (OKS-312) amber attention strip into a
 * lighter *preview* banner: no exit-form (it's a read-only view, not an
 * act-as session), just a "Back to {creator}" link to the crew detail
 * page. Persistent at the top of the previewed profile so the admin never
 * mistakes it for their own surface or an editable view (UX Spec §2).
 *
 * # Accessibility (UX Spec §6)
 *   - `role="status"` `aria-live="polite"` so AT announces the read-only
 *     context on load. Colour is NOT the sole indicator — the textual
 *     "Preview as creator — read only" label carries the meaning.
 *   - Long creator names truncate in the visible label; the full name is
 *     preserved in the `title` tooltip + the back link's `aria-label`.
 *   - Back link is keyboard-focusable with a ≥44px touch target on mobile.
 */
const NAME_MAX_CHARS = 24

export function ProfilePreviewBanner({
  creatorName,
  backUrl,
}: {
  creatorName: string
  backUrl: string | null
}) {
  const { t } = useTranslation()

  const truncatedName =
    creatorName.length > NAME_MAX_CHARS
      ? `${creatorName.slice(0, NAME_MAX_CHARS - 1)}…`
      : creatorName

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`${t("crew.profile_preview.banner.label")} — ${creatorName}`}
      data-testid="profile-preview-banner"
      className="relative z-30 w-full border-b border-amber-200 bg-amber-100 text-amber-900"
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-2 px-4 py-2 sm:h-10 sm:flex-row sm:items-center sm:gap-3 sm:py-0">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Eye aria-hidden="true" className="size-4 shrink-0" />
          <span className="text-sm font-semibold">
            {t("crew.profile_preview.banner.label")}
          </span>
          <span
            className="truncate text-sm opacity-80"
            title={creatorName}
            data-testid="profile-preview-banner-name"
          >
            {truncatedName}
          </span>
        </div>
        {backUrl && (
          <Link
            href={backUrl}
            data-testid="profile-preview-banner-back"
            aria-label={t("crew.profile_preview.banner.back", {
              name: creatorName,
            })}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1 text-sm font-medium underline-offset-4 hover:underline sm:min-h-0"
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
            {t("crew.profile_preview.banner.back", { name: truncatedName })}
          </Link>
        )}
      </div>
    </div>
  )
}
